import React, { useState, useEffect } from 'react';
import { X, Sun, Moon, User, LogOut, Mail, MessageCircle, Palette, HeadphonesIcon, FileText, Download } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import OptionsPanel from './OptionsPanel';
import InstallAppButton from './InstallAppButton';
import { Separator } from './ui/separator';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import LegalModal from './LegalModal';

const MenuDrawer = ({ 
  isOpen, 
  onOpenChange,
  category, 
  onCategoryChange,
  market,
  onMarketChange,
  markets,
  expos,
  festivals,
  exploreTab,
  onExploreTabChange,
  province,
  onProvinceChange,
  tags, 
  onTagsChange, 
  categories,
  sortBy,
  onSortChange,
  viewLayout,
  onViewChange,
  stallsPerPage,
  onStallsPerPageChange,
  theme,
  onThemeChange,
  onDashboardClick,
  onOpenVendorDashboard,
  onOpenAdminDashboard
}) => {
  const { user, isAuthenticated, logout, isAdmin, isVendor } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [legalModalType, setLegalModalType] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  // Detect if the app is running as an installed PWA — used to hide the
  // "Love WAYO" install-app section for users who already installed it.
  useEffect(() => {
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);
  }, []);

  // Check for ?login=true parameter in URL to auto-open login modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldOpenLogin = urlParams.get('login') === 'true';
    
    if (shouldOpenLogin && !isAuthenticated) {
      setShowAuthModal(true);
      // Clean up URL without reloading page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [isAuthenticated]);

  const handleAuthClick = () => {
    if (isAuthenticated) {
      onOpenChange(false);
      onDashboardClick?.();
    } else {
      setShowAuthModal(true);
    }
  };

  const handleLoginSuccess = () => {
    // Close the menu drawer and auth modal
    setShowAuthModal(false);
    onOpenChange(false);
    
    // Use a slightly longer delay to ensure auth state is fully updated
    setTimeout(() => {
      // Check user role directly from localStorage as a backup
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          if (userData.role === 'admin') {
            onOpenAdminDashboard?.();
          } else if (userData.role === 'vendor') {
            onOpenVendorDashboard?.();
          }
        } catch (e) {
          // Fallback to onDashboardClick
          onDashboardClick?.();
        }
      } else {
        onDashboardClick?.();
      }
    }, 150);
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    onOpenChange(false);
  };

  const openLegalModal = (type) => {
    setLegalModalType(type);
  };

  const closeLegalModal = () => {
    setLegalModalType(null);
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-[100vw] sm:w-[400px] sm:max-w-[540px] bg-white dark:bg-[#1a1a1a] overflow-y-auto [&>button]:hidden p-0">
        {/* Sticky Header with Close Button */}
        <div className="sticky top-0 z-10 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="relative flex items-center justify-center">
            <Button
              onClick={() => onOpenChange(false)}
              variant="ghost"
              className="absolute left-0 w-12 h-12 p-0 rounded-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] transition-all duration-300 shadow-md"
            >
              <X className="w-6 h-6" />
            </Button>
            <h2 className="text-2xl font-bold text-[#252525] dark:text-white">
              Menu
            </h2>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Auth Section - Moved to top */}
          <div className="space-y-3">
            {isAuthenticated ? (
              <>
                <div className="p-4 bg-[#FFD12D]/10 dark:bg-[#FFD12D]/20 rounded-lg border border-[#FFD12D]/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#FFD12D] flex items-center justify-center">
                      <User className="w-5 h-5 text-[#252525]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#252525] dark:text-white">
                        {user?.name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                        {user?.role}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={handleAuthClick}
                    className="bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold h-11 transition-all duration-300 shadow-md"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>

                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="border-2 border-gray-300 dark:border-gray-600 hover:border-red-500 dark:hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 font-semibold h-11 transition-all duration-300"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                  className="bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold h-11 transition-all duration-300 shadow-md"
                >
                  Log In
                </Button>
                <Button
                  onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}
                  variant="outline"
                  className="border-2 border-gray-300 dark:border-gray-600 hover:border-[#FFD12D] hover:bg-[#FFD12D]/10 text-gray-700 dark:text-gray-300 font-semibold h-11 transition-all duration-300"
                >
                  Sign Up
                </Button>
              </div>
            )}
          </div>

          <Separator className="bg-gray-200 dark:bg-gray-700" />

          {/* Options Panel */}
          <OptionsPanel
            category={category}
            onCategoryChange={onCategoryChange}
            market={market}
            onMarketChange={onMarketChange}
            markets={markets}
            expos={expos}
            festivals={festivals}
            exploreTab={exploreTab}
            onExploreTabChange={onExploreTabChange}
            province={province}
            onProvinceChange={onProvinceChange}
            tags={tags}
            onTagsChange={onTagsChange}
            categories={categories}
            sortBy={sortBy}
            onSortChange={onSortChange}
            viewLayout={viewLayout}
            onViewChange={onViewChange}
            stallsPerPage={stallsPerPage}
            onStallsPerPageChange={onStallsPerPageChange}
          />

          <Separator className="bg-gray-200 dark:bg-gray-700" />

          {/* Theme Section */}
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold text-[#252525] dark:text-white mb-4">
              <Palette className="w-5 h-5 text-[#FFD12D]" />
              <span>Theme</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => onThemeChange('light')}
                variant={theme === 'light' ? 'default' : 'outline'}
                className={`flex items-center justify-center gap-2 h-11 transition-all duration-300 ${
                  theme === 'light'
                    ? 'bg-[#FFD12D] text-[#252525] hover:bg-[#FFD12D] border-2 border-[#FFD12D]'
                    : 'border-2 border-gray-300 dark:border-gray-600 hover:border-[#FFD12D] hover:bg-[#FFD12D]/10 dark:text-gray-300'
                }`}
              >
                <Sun className="w-5 h-5" />
                <span className="font-semibold">Light</span>
              </Button>
              <Button
                onClick={() => onThemeChange('dark')}
                variant={theme === 'dark' ? 'default' : 'outline'}
                className={`flex items-center justify-center gap-2 h-11 transition-all duration-300 ${
                  theme === 'dark'
                    ? 'bg-[#FFD12D] text-[#252525] hover:bg-[#FFD12D] border-2 border-[#FFD12D]'
                    : 'border-2 border-gray-300 dark:border-gray-600 hover:border-[#FFD12D] hover:bg-[#FFD12D]/10 dark:text-gray-300'
                }`}
              >
                <Moon className="w-5 h-5" />
                <span className="font-semibold">Dark</span>
              </Button>
            </div>
          </div>

          <Separator className="bg-gray-200 dark:bg-gray-700" />

          {/* Support Section */}
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold text-[#252525] dark:text-white mb-4">
              <HeadphonesIcon className="w-5 h-5 text-[#FFD12D]" />
              <span>Support</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <a
                href="mailto:hello@wayo.co.za"
                className="flex items-center justify-center gap-2 h-11 px-4 border-2 border-gray-300 dark:border-gray-600 rounded-full hover:border-[#FFD12D] hover:bg-[#FFD12D]/10 transition-all duration-300 dark:text-gray-300 hover:text-[#252525] dark:hover:text-white"
              >
                <Mail className="w-5 h-5" />
                <span className="font-semibold">Email</span>
              </a>
              <a
                href="https://wa.me/27813787565?text=Hi%2C%20I%20need%20support."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 h-11 px-4 border-2 border-green-500 dark:border-green-600 rounded-full hover:bg-green-500 hover:text-white transition-all duration-300 dark:text-gray-300 text-green-600"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span className="font-semibold">WhatsApp</span>
              </a>
            </div>
          </div>

          <Separator className="bg-gray-200 dark:bg-gray-700" />

          {/* Love WAYO Section — hidden when already installed as PWA */}
          {!isStandalone && (
            <>
              <div>
                <div className="flex items-center gap-2 text-lg font-semibold text-[#252525] dark:text-white mb-4">
                  <Download className="w-5 h-5 text-[#FFD12D]" />
                  <span>Love WAYO</span>
                </div>
                <InstallAppButton />
              </div>

              <Separator className="bg-gray-200 dark:bg-gray-700" />
            </>
          )}

          {/* Legal Stuff Section */}
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold text-[#252525] dark:text-white mb-4">
              <FileText className="w-5 h-5 text-[#FFD12D]" />
              <span>Legal Stuff</span>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={() => openLegalModal('terms')}
                className="flex items-center justify-center gap-2 h-11 px-4 border-2 border-gray-300 dark:border-gray-600 rounded-full hover:border-[#FFD12D] hover:bg-[#FFD12D]/10 transition-all duration-300 dark:text-gray-300 hover:text-[#252525] dark:hover:text-white"
              >
                <span className="font-semibold">Terms and Conditions</span>
              </button>
              <button
                onClick={() => openLegalModal('privacy')}
                className="flex items-center justify-center gap-2 h-11 px-4 border-2 border-gray-300 dark:border-gray-600 rounded-full hover:border-[#FFD12D] hover:bg-[#FFD12D]/10 transition-all duration-300 dark:text-gray-300 hover:text-[#252525] dark:hover:text-white"
              >
                <span className="font-semibold">Privacy Policy</span>
              </button>
              <button
                onClick={() => openLegalModal('other')}
                className="flex items-center justify-center gap-2 h-11 px-4 border-2 border-gray-300 dark:border-gray-600 rounded-full hover:border-[#FFD12D] hover:bg-[#FFD12D]/10 transition-all duration-300 dark:text-gray-300 hover:text-[#252525] dark:hover:text-white"
              >
                <span className="font-semibold">Other</span>
              </button>
            </div>
          </div>
        </div>
      </SheetContent>
      
      <AuthModal 
        isOpen={showAuthModal} 
        onOpenChange={setShowAuthModal} 
        onLoginSuccess={handleLoginSuccess}
        initialMode={authMode}
      />

      <LegalModal
        type={legalModalType}
        isOpen={!!legalModalType}
        onClose={closeLegalModal}
      />

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <DialogContent className="sm:max-w-[400px] bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-0 overflow-hidden">
          <div className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FFD12D]/20 flex items-center justify-center">
              <LogOut className="w-8 h-8 text-[#FFD12D]" />
            </div>
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-xl font-bold text-[#252525] dark:text-white">
                Log Out
              </DialogTitle>
              <DialogDescription className="text-gray-500 dark:text-gray-400">
                Are you sure you want to log out of your account?
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-full h-11"
                data-testid="logout-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmLogout}
                className="flex-1 bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold rounded-full h-11"
                data-testid="logout-confirm-btn"
              >
                Yes, Logout
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Sheet>
    </>
  );
};

export default MenuDrawer;
